import React, { ReactElement } from "react";
import { gql, useQuery } from "urql";
import { Link } from "wouter";
import { Instrument, InstrumentModel, InstrumentModelTypeEnum } from "../types.generated";

interface ModelModalProps {
    isOpen: boolean;
    onClose: () => void;
    instrument: Pick<Instrument, "name" | "modelType" | "model">;
}

const GET_UNDERLYING_INSTRUMENT = gql`
    query getUnderlyingInstrument($id: GraphQLObjectId!) {
        instrument(_id: $id) {
            _id
            name
        }
    }
`;

const getModelSpecificFields = (modelType: InstrumentModelTypeEnum | null | undefined, model?: InstrumentModel | null, underlyingInstrumentName?: string) => {
    const fields: Array<{ 
        label: string; 
        value: string | number | boolean; 
        linkUrl?: string;
        linkName?: string;
        linkId?: string;
        tableData?: Array<{
            currency: string;
            amount: string;
        }>;
    }> = [];

    if (!model || !modelType) return fields;

    switch (modelType) {
        case InstrumentModelTypeEnum.Swaption: {
            const swaptionLeg = model.legs?.find((leg) => leg._t === "SwaptionLeg");
            const fixedAmountLeg = model.legs?.find((leg) => leg._t === "FixedAmountLeg");
            
            if (swaptionLeg) {
                fields.push({ label: "Expiry", value: swaptionLeg.expiry || "N/A" });
                const notionalValue = swaptionLeg.notional !== null && swaptionLeg.notional !== undefined 
                    ? swaptionLeg.notional.toLocaleString() 
                    : "N/A";
                fields.push({ label: "Notional", value: notionalValue });
                fields.push({ label: "Strike", value: swaptionLeg.strike || "N/A" });
                fields.push({ label: "Call", value: swaptionLeg.call ? "Yes" : "No" });
                fields.push({ label: "Exercise", value: swaptionLeg.exercise || "N/A" });
                fields.push({ label: "Swaption leg payleg", value: swaptionLeg.payLeg !== undefined ? (swaptionLeg.payLeg ? "Yes" : "No") : "N/A" });
                let underlyingValue = "N/A";
                const underlyingField: { label: string; value: string; linkUrl?: string; linkName?: string; linkId?: string } = { 
                    label: "Underlying", 
                    value: underlyingValue 
                };
                if (swaptionLeg.underlyingId) {
                    if (underlyingInstrumentName) {
                        underlyingValue = `${underlyingInstrumentName} (${swaptionLeg.underlyingId})`;
                        underlyingField.linkUrl = `/instruments/${swaptionLeg.underlyingId}`;
                        underlyingField.linkName = underlyingInstrumentName;
                        underlyingField.linkId = swaptionLeg.underlyingId;
                    } else {
                        underlyingValue = swaptionLeg.underlyingId;
                    }
                }
                underlyingField.value = underlyingValue;
                fields.push(underlyingField);
                fields.push({ label: "Volatility", value: swaptionLeg.volatility || "N/A" });
                fields.push({ label: "Settlement Type", value: swaptionLeg.settlementType || "N/A" });
            }
            
            if (fixedAmountLeg && fixedAmountLeg.cashFlows && fixedAmountLeg.cashFlows.length > 0) {
                const firstCashFlow = fixedAmountLeg.cashFlows[0];
                const premiumValue = firstCashFlow.notional ? firstCashFlow.notional.toLocaleString() : "N/A";
                fields.push({ label: "Premium", value: premiumValue });
                fields.push({ label: "Premium Ex Date", value: firstCashFlow.exDate || "N/A" });
                fields.push({ label: "Premium Payment Date", value: firstCashFlow.payDate || "N/A" });
            }
            break;
        }
            
        case InstrumentModelTypeEnum.Swap: {
            const swapLegs = model.legs || [];
            const fixedLeg = swapLegs.find((leg) => leg._t === "FixedRateLeg");
            const floatLeg = swapLegs.find((leg) => leg._t === "FloatRateLeg");
            
            // Get notional from cashFlows - appear right after Name
            let notionalValue = "N/A";
            const legWithCashFlows = swapLegs.find((leg) => leg.cashFlows && leg.cashFlows.length > 0);
            if (legWithCashFlows && legWithCashFlows.cashFlows[0]?.notional !== undefined) {
                notionalValue = legWithCashFlows.cashFlows[0].notional.toLocaleString();
            }
            fields.push({ label: "Notional", value: notionalValue });
            
            // Currency from legs
            const legWithCurrency = swapLegs.find((leg) => leg.currency);
            if (legWithCurrency) {
                fields.push({ label: "Currency", value: legWithCurrency.currency || "N/A" });
            }
            
            // Side: Receiver if FixedRateLeg has payLeg=False, else Payer
            const sideValue = fixedLeg && fixedLeg.payLeg === false ? "Receiver" : "Payer";
            fields.push({ label: "Side", value: sideValue });
            
            // Fixed Rate
            if (fixedLeg) {
                fields.push({ label: "Fixed Rate", value: fixedLeg.rate || "N/A" });
            }
            
            // Start date and End date from any leg
            const legWithDates = swapLegs.find((leg) => leg.startDate || leg.maturityDate);
            if (legWithDates) {
                fields.push({ label: "Start Date", value: legWithDates.startDate || "N/A" });
                fields.push({ label: "End Date", value: legWithDates.maturityDate || "N/A" });
            }
            
            // Floating index from FloatRateLeg
            if (floatLeg && floatLeg.rateIndexId) {
                fields.push({ label: "Floating Index", value: floatLeg.rateIndexId });
            }
            
            // Last fixing: latest non-null rate from FloatRateLeg cashFlows Fixings
            if (floatLeg && floatLeg.cashFlows && floatLeg.cashFlows.length > 0) {
                const allFixings = [];
                for (const cashFlow of floatLeg.cashFlows) {
                    const fixings = cashFlow.fixings;
                    if (fixings && Array.isArray(fixings)) {
                        for (const fixing of fixings) {
                            if (fixing.rate !== null && fixing.rate !== undefined) {
                                allFixings.push(fixing);
                            }
                        }
                    }
                }
                
                if (allFixings.length > 0) {
                    // Get the latest fixing (assuming they're ordered or we need to sort by date)
                    const lastFixing = allFixings[allFixings.length - 1];
                    fields.push({ label: "Last Fixing", value: lastFixing.rate || "N/A" });
                    fields.push({ label: "Last Fixing Date", value: lastFixing.date || "N/A" });
                }
            }
            break;
        }
            
        case InstrumentModelTypeEnum.Bond: {
            const bondLegs = model.legs || [];
            const couponLeg = bondLegs.find((leg) => leg._t === "FixedRateLeg");
            const floatLeg = bondLegs.find((leg) => leg._t === "FloatRateLeg");
            
            // Currency from legs - appear right after Name
            const legWithCurrency = bondLegs.find((leg) => leg.currency);
            if (legWithCurrency) {
                fields.push({ label: "Currency", value: legWithCurrency.currency || "N/A" });
            }
            
            // Start date from FloatRateLeg or FixedRateLeg - appear below Currency
            const legWithStartDate = floatLeg || couponLeg;
            if (legWithStartDate && legWithStartDate.startDate) {
                fields.push({ label: "Start Date", value: legWithStartDate.startDate || "N/A" });
            }
            
            // Maturity - appear below Currency
            fields.push({ label: "Maturity", value: bondLegs[0]?.maturityDate || "N/A" });
            
            // Coupon Rate - appear below Currency
            if (couponLeg) {
                let couponRateValue = "N/A";
                if (couponLeg.rate !== null && couponLeg.rate !== undefined) {
                    const percentage = (couponLeg.rate * 100).toFixed(2);
                    couponRateValue = `${percentage}%`;
                }
                fields.push({ label: "Coupon Rate", value: couponRateValue });
            }
            
            // Type: Fixed if any leg has type FixedRateLeg, else Floating
            const typeValue = couponLeg ? "Fixed" : "Floating";
            fields.push({ label: "Type", value: typeValue });
            
            // Day count from FloatRateLeg or FixedRateLeg - appear below Type
            const legWithDayCount = floatLeg || couponLeg;
            if (legWithDayCount && legWithDayCount.dayCount) {
                fields.push({ label: "Day Count", value: legWithDayCount.dayCount || "N/A" });
            }
            
            // Floating index if type is Floating
            if (typeValue === "Floating" && floatLeg && floatLeg.rateIndexId) {
                fields.push({ label: "Floating Index", value: floatLeg.rateIndexId });
            }
            
            // Spread if type is Floating
            if (typeValue === "Floating" && floatLeg && floatLeg.spread !== undefined && floatLeg.spread !== null) {
                const spreadBp = (floatLeg.spread * 10000).toString();
                fields.push({ label: "Spread", value: `${spreadBp} bp` });
            }
            
            // Current coupon from FloatRateLeg: latest cashFlow with non-null rate
            if (floatLeg && floatLeg.cashFlows && floatLeg.cashFlows.length > 0) {
                let currentCouponValue: number | string = "N/A";
                let currentCouponExDate = "N/A";
                let currentCouponPayDate = "N/A";
                let currentCouponIndex = -1;
                
                // Find the latest cashFlow with a non-null rate (iterate backwards)
                for (let i = floatLeg.cashFlows.length - 1; i >= 0; i--) {
                    const cashFlow = floatLeg.cashFlows[i];
                    const fixings = cashFlow.fixings;
                    
                    // Check fixings first
                    if (fixings && fixings.length > 0) {
                        for (let j = fixings.length - 1; j >= 0; j--) {
                            if (fixings[j].rate !== null && fixings[j].rate !== undefined) {
                                currentCouponValue = fixings[j].rate;
                                currentCouponExDate = cashFlow.exDate || "N/A";
                                currentCouponPayDate = cashFlow.payDate || "N/A";
                                currentCouponIndex = i;
                                break;
                            }
                        }
                        if (currentCouponValue !== "N/A") break;
                    }
                    
                    // Check cashFlow.rate if no fixing rate found (fallback for runtime data)
                    const cashFlowWithRate = cashFlow as typeof cashFlow & { rate?: number | null };
                    if (cashFlowWithRate.rate !== null && cashFlowWithRate.rate !== undefined) {
                        currentCouponValue = cashFlowWithRate.rate;
                        currentCouponExDate = cashFlow.exDate || "N/A";
                        currentCouponPayDate = cashFlow.payDate || "N/A";
                        currentCouponIndex = i;
                        break;
                    }
                }
                
                // Previous coupon from FloatRateLeg: one step back from current coupon cashFlow
                let previousCouponValue: number | string = "N/A";
                let previousCouponExDate = "N/A";
                let previousCouponPayDate = "N/A";
                
                // Start from one index before where we found the current coupon
                if (currentCouponIndex > 0) {
                    for (let i = currentCouponIndex - 1; i >= 0; i--) {
                        const cashFlow = floatLeg.cashFlows[i];
                        const fixings = cashFlow.fixings;
                        
                        // Check fixings first
                        if (fixings && fixings.length > 0) {
                            for (let j = fixings.length - 1; j >= 0; j--) {
                                if (fixings[j].rate !== null && fixings[j].rate !== undefined) {
                                    previousCouponValue = fixings[j].rate;
                                    previousCouponExDate = cashFlow.exDate || "N/A";
                                    previousCouponPayDate = cashFlow.payDate || "N/A";
                                    break;
                                }
                            }
                            if (previousCouponValue !== "N/A") break;
                        }
                        
                        // Check cashFlow.rate if no fixing rate found (fallback for runtime data)
                        const cashFlowWithRate = cashFlow as typeof cashFlow & { rate?: number | null };
                        if (cashFlowWithRate.rate !== null && cashFlowWithRate.rate !== undefined) {
                            previousCouponValue = cashFlowWithRate.rate;
                            previousCouponExDate = cashFlow.exDate || "N/A";
                            previousCouponPayDate = cashFlow.payDate || "N/A";
                            break;
                        }
                    }
                }
                
                // Format as percentage
                const previousCouponDisplay = previousCouponValue !== "N/A" 
                    ? `${((previousCouponValue as number) * 100).toFixed(2)}%`
                    : "N/A";
                const currentCouponDisplay = currentCouponValue !== "N/A"
                    ? `${((currentCouponValue as number) * 100).toFixed(2)}%`
                    : "N/A";
                
                fields.push({ label: "Previous Coupon", value: previousCouponDisplay });
                fields.push({ label: "Previous Coupon Ex Date", value: previousCouponExDate });
                fields.push({ label: "Previous Coupon Payment Date", value: previousCouponPayDate });
                fields.push({ label: "Current Coupon", value: currentCouponDisplay });
                fields.push({ label: "Current Coupon Ex Date", value: currentCouponExDate });
                fields.push({ label: "Current Coupon Payment Date", value: currentCouponPayDate });
            }
            
            // Current Coupon ex date and Next Coupon ex date for FixedRate bonds
            if (couponLeg) {
                if (couponLeg.cashFlows && couponLeg.cashFlows.length > 0) {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    // Find the cashFlow with exDate closest to today's date
                    let currentCouponIndex = -1;
                    let minDateDiff = Infinity;
                    
                    for (let i = 0; i < couponLeg.cashFlows.length; i++) {
                        const cashFlow = couponLeg.cashFlows[i];
                        if (cashFlow.exDate) {
                            const exDate = new Date(cashFlow.exDate);
                            exDate.setHours(0, 0, 0, 0);
                            const dateDiff = Math.abs(exDate.getTime() - today.getTime());
                            
                            if (dateDiff < minDateDiff) {
                                minDateDiff = dateDiff;
                                currentCouponIndex = i;
                            }
                        }
                    }
                    
                    let currentCouponExDate = "N/A";
                    let nextCouponExDate = "N/A";
                    
                    if (currentCouponIndex >= 0) {
                        const currentCashFlow = couponLeg.cashFlows[currentCouponIndex];
                        currentCouponExDate = currentCashFlow?.exDate || "N/A";
                        
                        // Next is the next item in cashFlows array after current
                        if (currentCouponIndex < couponLeg.cashFlows.length - 1) {
                            const nextCashFlow = couponLeg.cashFlows[currentCouponIndex + 1];
                            nextCouponExDate = nextCashFlow?.exDate || "N/A";
                        }
                    }
                    
                    fields.push({ label: "Current Coupon Ex Date", value: currentCouponExDate });
                    fields.push({ label: "Next Coupon Ex Date", value: nextCouponExDate });
                } else {
                    fields.push({ label: "Current Coupon Ex Date", value: "N/A" });
                    fields.push({ label: "Next Coupon Ex Date", value: "N/A" });
                }
            }
            break;
        }
            
        case InstrumentModelTypeEnum.Stock:
        case InstrumentModelTypeEnum.RealEstate:
        case InstrumentModelTypeEnum.PrivateEquity:
            fields.push({ label: "Quote Currency", value: model.quoteCurrency || "N/A" });
            fields.push({ label: "Quote Type", value: model.quoteType || "N/A" });
            break;
            
        case InstrumentModelTypeEnum.Balance:
            fields.push({ label: "Currency", value: model.quoteCurrency || "N/A" });
            break;
            
        case InstrumentModelTypeEnum.FundExternal:
        case InstrumentModelTypeEnum.FundInternal:
            fields.push({ label: "Quote Currency", value: model.quoteCurrency || "N/A" });
            fields.push({ label: "Quote Type", value: model.quoteType || "N/A" });
            break;
            
        case InstrumentModelTypeEnum.FxSwap: {
            // FxSwap model type - display cashFlows in table format
            const cashFlowLegs = model.legs?.filter((leg) => leg._t === "FixedAmountLeg") || [];
            console.log("cashFlowLegs", cashFlowLegs);
            
            // Add ex date and pay date as separate fields using first item
            if (cashFlowLegs.length > 0) {
                const firstLeg = cashFlowLegs[0];
                const firstCashFlow = firstLeg.cashFlows?.[0];
                
                if (firstCashFlow) {
                    fields.push({ label: "Ex Date", value: firstCashFlow.exDate || "N/A" });
                    fields.push({ label: "Pay Date", value: firstCashFlow.payDate || "N/A" });
                }
                
                // Add table data for FxSwap (only currency and amount)
                fields.push({ 
                    label: "FxSwap Table", 
                    value: "table", 
                    tableData: cashFlowLegs.map((leg) => {
                        const firstCashFlow = leg.cashFlows?.[0];
                        
                        let amount = firstCashFlow?.notional !== undefined && firstCashFlow?.notional !== null 
                            ? firstCashFlow.notional.toLocaleString() 
                            : (leg.notional !== undefined && leg.notional !== null 
                                ? leg.notional.toLocaleString() 
                                : "N/A");
                        
                        // Add minus sign if payLeg is true
                        if (amount !== "N/A" && leg.payLeg === true) {
                            amount = "-" + amount;
                        }
                        
                        return {
                            currency: leg.currency || "N/A",
                            amount: amount
                        };
                    })
                });
            }
            break;
        }
            
        case InstrumentModelTypeEnum.PortfolioSwapX:
            fields.push({ label: "Start Date", value: model.startDate || "N/A" });
            fields.push({ label: "Expiry Date", value: model.expiryDate || "N/A" });
            fields.push({ label: "Notional Scaling", value: model.notionalScaling || "N/A" });
            break;
            
        case InstrumentModelTypeEnum.CdsIndex: {
            // CdsIndex: extract fields from FixedRateLeg
            const cdsIndexLegs = model.legs || [];
            const fixedRateLeg = cdsIndexLegs.find((leg) => leg._t === "FixedRateLeg");
            
            if (fixedRateLeg) {
                // Start date from FixedRateLeg
                if (fixedRateLeg.startDate) {
                    fields.push({ label: "Start Date", value: fixedRateLeg.startDate });
                }
                
                // Maturity date from FixedRateLeg
                if (fixedRateLeg.maturityDate) {
                    fields.push({ label: "Maturity Date", value: fixedRateLeg.maturityDate });
                }
                
                // Currency from FixedRateLeg
                if (fixedRateLeg.currency) {
                    fields.push({ label: "Currency", value: fixedRateLeg.currency });
                }
                
                // Coupon rate from FixedRateLeg (show as percent)
                if (fixedRateLeg.rate !== null && fixedRateLeg.rate !== undefined) {
                    const couponPercent = (fixedRateLeg.rate * 100).toFixed(2);
                    fields.push({ label: "Coupon", value: `${couponPercent}%` });
                }
                
                // Recovery rate from FixedRateLeg (show as percent)
                if (fixedRateLeg.recoveryRate !== null && fixedRateLeg.recoveryRate !== undefined) {
                    const recoveryPercent = (fixedRateLeg.recoveryRate * 100).toFixed(2);
                    fields.push({ label: "Recovery Rate", value: `${recoveryPercent}%` });
                }
            }
            break;
        }
            
        case InstrumentModelTypeEnum.CdsBasket: {
            // CdsBasket: extract fields from FixedRateLeg
            const cdsBasketLegs = model.legs || [];
            const fixedRateLeg = cdsBasketLegs.find((leg) => leg._t === "FixedRateLeg");
            
            if (fixedRateLeg) {
                // Start date from FixedRateLeg
                if (fixedRateLeg.startDate) {
                    fields.push({ label: "Start Date", value: fixedRateLeg.startDate });
                }
                
                // Maturity date from FixedRateLeg
                if (fixedRateLeg.maturityDate) {
                    fields.push({ label: "Maturity Date", value: fixedRateLeg.maturityDate });
                }
                
                // Currency from FixedRateLeg
                if (fixedRateLeg.currency) {
                    fields.push({ label: "Currency", value: fixedRateLeg.currency });
                }
                
                // Coupon rate from FixedRateLeg (show as percent)
                if (fixedRateLeg.rate !== null && fixedRateLeg.rate !== undefined) {
                    const couponPercent = (fixedRateLeg.rate * 100).toFixed(2);
                    fields.push({ label: "Coupon", value: `${couponPercent}%` });
                }
                
                // Recovery rate from FixedRateLeg (show as percent)
                if (fixedRateLeg.recoveryRate !== null && fixedRateLeg.recoveryRate !== undefined) {
                    const recoveryPercent = (fixedRateLeg.recoveryRate * 100).toFixed(2);
                    fields.push({ label: "Recovery Rate", value: `${recoveryPercent}%` });
                }
            }
            break;
        }
            
        default:
            // Generic fields for unknown model types
            if (model.startDate) fields.push({ label: "Start Date", value: model.startDate });
            if (model.expiryDate) fields.push({ label: "Expiry Date", value: model.expiryDate });
            break;
    }
    
    return fields;
};

export const ModelModal = ({ isOpen, onClose, instrument }: ModelModalProps): ReactElement => {
    // Extract underlyingId from Swaption leg if it exists
    const swaptionLeg = instrument.model?.legs?.find((leg) => leg._t === "SwaptionLeg");
    const underlyingId = swaptionLeg?.underlyingId;

    // Query underlying instrument if underlyingId exists
    const [{ data: underlyingInstrumentData }] = useQuery({
        query: GET_UNDERLYING_INSTRUMENT,
        variables: { id: underlyingId },
        pause: !isOpen || !underlyingId || instrument.modelType !== InstrumentModelTypeEnum.Swaption
    });

    const underlyingInstrumentName = underlyingInstrumentData?.instrument?.name;

    if (!isOpen) return null;

    const modelSpecificFields = getModelSpecificFields(instrument.modelType || null, instrument.model || null, underlyingInstrumentName);

    return (
        <div 
            role="dialog" 
            className="model-modal" 
            style={{
                position: "fixed",
                top: "20px",
                bottom: "20px",
                right: "0px",
                left: "0px",
                width: "40%",
                maxWidth: "40%",
                height: "calc(100vh - 40px)",
                backgroundColor: "white",
                boxShadow: "0px 25.6px 57.6px 0px #00000038, 0px 4.8px 14.4px 0px #0000002e",
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                margin: "0 auto"
            }}
        >
            <header style={{
                display: "flex",
                flexDirection: "column",
                position: "relative",
                paddingTop: "30px",
                paddingLeft: "20px",
                paddingRight: "20px",
                width: "100%",
                height: "auto",
                minHeight: "60px"
            }}>
                <h2 style={{ fontSize: "18px" }}>Model Details</h2>
                <button 
                    id="close" 
                    className="close-btn" 
                    style={{
                        position: "absolute",
                        right: "20px",
                        bottom: "0px",
                        fontSize: "24px",
                        padding: "0px",
                        background: "none",
                        border: "none",
                        cursor: "pointer"
                    }} 
                    onClick={onClose}
                >
                    ×
                </button>
            </header>
            <div style={{
                overflowX: "hidden",
                padding: "20px",
                flex: 1
            }}>
                <div className="model-details">
                    <div className="w-full mb-1 flex items-center">
                        <label className="form-label font-bold mr-3 mb-0" style={{ minWidth: "150px" }}>Type:</label>
                        <div className="form-control-plaintext" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{instrument.modelType || "N/A"}</div>
                    </div>
                    
                    <div className="w-full mb-1 flex items-center">
                        <label className="form-label font-bold mr-3 mb-0" style={{ minWidth: "150px" }}>Name:</label>
                        <div className="form-control-plaintext" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{instrument.name || "N/A"}</div>
                    </div>
                    
                    {modelSpecificFields.map((field, index) => (
                        field.value === "table" && field.tableData ? (
                            <div key={index} className="w-full mb-3">
                                <label className="form-label font-bold mb-2">{field.label}:</label>
                                <div className="table-responsive">
                                    <table className="table table-sm table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Currency</th>
                                                <th>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {field.tableData.map((row, rowIndex) => (
                                                <tr key={rowIndex}>
                                                    <td>{row.currency}</td>
                                                    <td>{row.amount}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div key={index} className="w-full mb-1 flex items-center">
                                <label className="form-label font-bold mr-3 mb-0" style={{ minWidth: "150px" }}>{field.label}:</label>
                                <div className="form-control-plaintext" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {field.linkUrl && field.linkName && field.linkId ? (
                                        <>
                                            <Link to={field.linkUrl}>{field.linkName}</Link>
                                            {` (${field.linkId})`}
                                        </>
                                    ) : field.linkUrl ? (
                                        <Link to={field.linkUrl}>{field.value}</Link>
                                    ) : (
                                        field.value
                                    )}
                                </div>
                            </div>
                        )
                    ))}
                    
                </div>
            </div>
        </div>
    );
};
